import React, { useId } from "react";
import { Link as GatsbyLink } from "gatsby";
import { useLocation } from "@reach/router";
import { useStaticQuery, graphql } from "gatsby";
import URI from "urijs";
import { useI18n } from "@qlik/utils/i18nContext";
import { usePageList } from "@qlik/utils/pageListContext";
import { normalizePath } from "@qlik/utils/normalizePathOrUrl";
import { Tooltip } from "react-tooltip";
import { enOnly as enOnlyTranslations } from "@qlik/qlik/src/translations/enOnly";

const Link = ({ children, to, newWindow, activeClassName, ...props }) => {
    // We need to find out on which domain we are, so we can properly set if a link is an external link or not.
    const { site } = useStaticQuery(
        graphql`
            query {
                site {
                    siteMetadata {
                        siteProductionDomain
                    }
                }
            }
        `
    );

    // Grab the page list (all published pages on the site) so that we can determine if this is a multilingual
    // link that's pointing to a page that isn't published in it's language (in which case we'll fall back to english).
    const { pageList, redirectList } = usePageList();

    // Grab the i18n helper.
    const { i18n } = useI18n();

    // Create a unique ID for each link (currently only used for i18n links that point back to the english site, see below).
    const linkUniqueId = useId();

    // Get current Location Object from the router
    const location = useLocation();

    // If there's no "to" then return an anchor tag with the child text & no href.
    // (span would require major CSS overhaul, @todo fix it)
    if (!to) return <a {...props}>{children}</a>;

    // Create URI object from "to" for convenience (access to query string, fragment, etc).
    let uri = URI(to);

    // If this link is internal to the current gatsby site, use GatsbyLink component.
    const isInternal = !["mailto", "http", "https", "ftp", "ftps", "tel", "javascript"].includes(uri.protocol());

    // Determine if the destination is a jump link (if so we will use a standard anchor tag).
    const isJumpLink = to === "#" || uri.fragment();

    // Switch to "destination" so that we can prepend the langcode to the path (if necessary) without altering the "to" prop.
    let destination = to;

    // If the destination is not external and the language is not explicitly set via
    // an existing language prefix, prepend the current language using the i18n helper.
    // (excluding paths where `to` starts with # to maintain consistency and help with automated testing of new links vs old)
    if (isInternal && !i18n.pathHasLangPrefix(destination) && !destination?.startsWith("#")) {
        destination = i18n.getLangPrefixedPath(uri.path());
        // If language prefixes has been added recreate the URI object
        uri = URI(`${destination}?${uri.query()}#${uri.fragment()}`);
        // Normalize the path component (potentially removing extra slashes) and set the "to" variable to "resource" (path + query + fragment).
        destination = uri.resource();
    }

    // If the link destination doesn't exist as a page or redirect, but it does exist (as page or redirect) in english,
    // use the english path as a fallback.
    let isChangingLanguage = false;
    if (
        isInternal &&
        !pageList?.[uri?.path()] &&
        !redirectList?.[uri?.path()] &&
        (pageList?.[normalizePath(`${i18n.map.en.pathPrefix}/${i18n.getUnPrefixedPath(uri?.path())}`)] ||
            redirectList?.[normalizePath(`${i18n.map.en.pathPrefix}/${i18n.getUnPrefixedPath(uri?.path())}`)])
    ) {
        destination = normalizePath(`${i18n.map.en.pathPrefix}/${i18n.getUnPrefixedPath(to)}`);
        isChangingLanguage = true;
    }

    // Determine if the link is pointing to the current page (in which case we'll use a standard anchor tag).
    const isCurrentPage = (destination && uri.path() === location.pathname) || (!uri.path() && isJumpLink);

    // If we're linking to the current page don't add the tooltip.
    if (isCurrentPage) isChangingLanguage = false;

    // Determine if the link should open in a new window
    let shouldOpenNewWindow = false;
    const matchDomain = RegExp(`^((f|ht)tps?:)?//(${location.host}|${site.siteMetadata.siteProductionDomain})`).test(
        destination
    );

    if (newWindow === true) {
        shouldOpenNewWindow = true;
    } else if (newWindow !== false) {
        // Only apply auto-detection if newWindow is not explicitly false
        if (!isInternal && !matchDomain && uri.protocol() !== "javascript") {
            shouldOpenNewWindow = true;
        }
    }
    // Ensure javascript protocol never opens a new window regardless of other settings
    if (uri.protocol() === "javascript") {
        shouldOpenNewWindow = false;
    }

    // Determine if Gatsby Link component should be used
    // Condition: Internal, not a jump link, not the current page, AND not opening in a new window
    const useGatsbyLink = isInternal && !isJumpLink && !isCurrentPage && !shouldOpenNewWindow;

    // --- Prepare Attributes ---

    const newWindowAttributes = shouldOpenNewWindow ? { rel: "noopener noreferrer", target: "_blank" } : {};

    const jumpLinkAttributes =
        isJumpLink && isCurrentPage && uri.fragment()
            ? {
                  onClick: (e) => {
                      e.preventDefault();
                      document.querySelector(`#${uri.fragment()}`).scrollIntoView({ behavior: "smooth" });
                  },
              }
            : {};

    const tooltipId = `tooltip-id-${linkUniqueId}`;
    const tooltipAttributes = isChangingLanguage ? { "data-tooltip-id": tooltipId } : {};

    // --- Render Component ---

    const commonProps = {
        ...props, // Spread remaining props first
        ...tooltipAttributes,
    };

    return (
        <>
            {useGatsbyLink ? (
                <GatsbyLink to={destination} activeClassName={activeClassName} {...commonProps}>
                    {children}
                </GatsbyLink>
            ) : (
                <a href={destination} {...commonProps} {...newWindowAttributes} {...jumpLinkAttributes}>
                    {children}
                </a>
            )}
            {isChangingLanguage && (
                <Tooltip id={tooltipId} place="right" className="z-popup">
                    {enOnlyTranslations?.[i18n?.curr?.contentfulCode]?.enOnly}
                </Tooltip>
            )}
        </>
    );
};

export default Link;
